import { ZONE_TYPES, type ZoneListQuery, type ZoneStatus, type ZoneType } from '@chukua/common';
import { Select } from 'antd';
import { ZONE_TYPE_LABELS } from '../../../policies/zone-labels';
import { ZoneKindSwatch } from '../../../../../shared/components/ZoneKindSwatch';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import type { ZoneKind } from '../../../../reference-data/reference-data.api';
import { ZonesMapSearch } from './ZonesMapSearch';

interface ZonesMapToolbarProps {
  list: Pick<ListQueryControls<ZoneListQuery>, 'query' | 'setQuery'>;
  zoneKinds: ZoneKind[];
  onPickZone: (zoneId: string) => void;
}

const STATUS_OPTIONS: { value: ZoneStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

/** Search (suggesting matching zones) and filters above the map. They are the table's too, so switching views keeps them. */
export function ZonesMapToolbar({ list, zoneKinds, onPickZone }: ZonesMapToolbarProps) {
  const kindOptions = zoneKinds.map((kind) => ({
    value: kind.code,
    label: (
      <span className="flex items-center gap-2">
        <ZoneKindSwatch color={kind.color} />
        {kind.name}
      </span>
    ),
  }));

  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center">
      <div className="md:w-72">
        <ZonesMapSearch filter={list.query.q} onFilter={(q) => list.setQuery({ q }, { replace: true })} onPickZone={onPickZone} />
      </div>
      <Select<string[]>
        mode="multiple"
        allowClear
        maxTagCount="responsive"
        placeholder="All kinds"
        aria-label="Kind"
        className="md:w-64"
        options={kindOptions}
        value={list.query.kind ?? []}
        onChange={(kind) => list.setQuery({ kind: kind.length ? kind : undefined }, { replace: true })}
      />
      <Select<ZoneType[]>
        mode="multiple"
        allowClear
        placeholder="Places and boundaries"
        aria-label="Type"
        className="md:w-56"
        options={ZONE_TYPES.map((type) => ({ value: type, label: ZONE_TYPE_LABELS[type] }))}
        value={list.query.type ?? []}
        onChange={(type) => list.setQuery({ type: type.length ? type : undefined }, { replace: true })}
      />
      <Select<ZoneStatus[]>
        mode="multiple"
        allowClear
        placeholder="Any status"
        aria-label="Status"
        className="md:w-44"
        options={STATUS_OPTIONS}
        value={list.query.status ?? []}
        onChange={(status) => list.setQuery({ status: status.length ? status : undefined }, { replace: true })}
      />
    </div>
  );
}
